import React, { useEffect, useState } from 'react';
import { BasemapId } from '../../lib/types';
import { BASEMAPS } from '../../lib/basemaps';

interface BasemapPickerModalProps {
  open: boolean;
  currentId?: BasemapId;
  onPick: (id: BasemapId) => void;
  onClose: () => void;
}

const kindLabel: Record<'imagery' | 'thematic', string> = {
  imagery: 'Imagery',
  thematic: 'Thematic',
};

const kindBadgeStyles: Record<'imagery' | 'thematic', string> = {
  imagery: 'bg-figma-lime/20 text-figma-lime',
  thematic: 'bg-figma-yellow/20 text-figma-yellow',
};

export const BasemapPickerModal: React.FC<BasemapPickerModalProps> = ({
  open,
  currentId,
  onPick,
  onClose,
}) => {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[2000] overflow-y-auto bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="min-h-full flex items-center justify-center px-4 py-6">
      <div
        className="relative w-full max-w-3xl bg-figma-panel rounded-figma-panel shadow-figma-card border border-figma-lime/20 p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close basemap picker"
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-figma-text hover:bg-figma-card transition-smooth"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        <h2 className="font-roboto font-bold text-title-lg text-figma-text">Choose a basemap</h2>
        <p className="mt-2 font-roboto text-body text-neutral-400">
          Switch the map&rsquo;s base layer. You can change this again anytime from the layers button in
          the top-right of the map.
        </p>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.values(BASEMAPS).map((basemap) => {
            const selected = currentId === basemap.id;
            return (
              <BasemapCard
                key={basemap.id}
                id={basemap.id}
                displayName={basemap.displayName}
                description={basemap.description}
                kind={basemap.kind}
                thumbnailPath={basemap.thumbnailPath}
                attribution={basemap.attribution}
                selected={selected}
                onPick={onPick}
              />
            );
          })}
        </div>
      </div>
      </div>
    </div>
  );
};

interface BasemapCardProps {
  id: BasemapId;
  displayName: string;
  description: string;
  kind: 'imagery' | 'thematic';
  thumbnailPath: string;
  attribution?: string;
  selected: boolean;
  onPick: (id: BasemapId) => void;
}

const BasemapCard: React.FC<BasemapCardProps> = ({
  id,
  displayName,
  description,
  kind,
  thumbnailPath,
  attribution,
  selected,
  onPick,
}) => {
  const [imgBroken, setImgBroken] = useState(false);

  return (
    <button
      onClick={() => onPick(id)}
      aria-pressed={selected}
      className={`group flex flex-col text-left bg-figma-card rounded-figma-button overflow-hidden border transition-smooth focus:outline-none focus:ring-2 focus:ring-figma-yellow ${
        selected
          ? 'border-figma-yellow ring-2 ring-figma-yellow'
          : 'border-figma-lime/20 hover:border-figma-lime/60'
      }`}
    >
      <div className="px-3 py-2 bg-figma-surface flex items-center">
        <span
          className={`px-2 py-0.5 rounded text-[10px] font-semibold tracking-wider uppercase ${kindBadgeStyles[kind]}`}
        >
          {kindLabel[kind]}
        </span>
      </div>

      <div className="relative aspect-[16/9] w-full bg-figma-surface">
        {!imgBroken ? (
          <img
            src={thumbnailPath}
            alt=""
            className="w-full h-full object-cover"
            onError={() => setImgBroken(true)}
          />
        ) : (
          <BasemapThumbnailFallback kind={kind} />
        )}
      </div>

      <div className="flex flex-col flex-1 p-4 space-y-1">
        <div className="font-roboto font-bold text-body text-figma-text group-hover:text-figma-lime transition-smooth">
          {displayName}
        </div>
        <p className="font-roboto text-sm text-neutral-400 leading-snug">{description}</p>
        {attribution && (
          <p
            className="mt-auto pt-3 text-xs text-neutral-500 font-roboto leading-snug [&_a]:underline [&_a:hover]:text-figma-lime"
            dangerouslySetInnerHTML={{ __html: attribution }}
          />
        )}
      </div>
    </button>
  );
};

const BasemapThumbnailFallback: React.FC<{ kind: 'imagery' | 'thematic' }> = ({ kind }) => {
  const bg =
    kind === 'imagery'
      ? 'bg-gradient-to-br from-slate-600 via-slate-700 to-slate-900'
      : 'bg-gradient-to-br from-emerald-700 via-emerald-500 to-yellow-300';
  return (
    <div className={`w-full h-full ${bg} flex items-center justify-center`}>
      <svg width="42" height="42" viewBox="0 0 24 24" fill="none" className="opacity-60">
        <path
          d="M21 3l-6 2-6-2-6 2v16l6-2 6 2 6-2V3zM9 18.4L4 19.9V6.4l5-1.5v13.5zm6 0l-4-1V4.9l4 1v12.5z"
          fill="white"
        />
      </svg>
    </div>
  );
};
